// IdCard.jsx
import 'assets/resources/comtrue/css/default/common.css'
import 'assets/resources/comtrue/css/default/ocr_common.css'
import 'assets/resources/comtrue/css/default/ocr_common.min.css'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { COMTRUEVAR } from 'assets/resources/comtrue/js/default/ocr_variables'
import { useCOMTRUEOcrCommon } from 'assets/resources/comtrue/js/default/ocr_common'
import { useComtrueCamera } from 'assets/resources/comtrue/js/camera/camera'
import { useComtrueRefs } from 'assets/resources/comtrue/js/default/useComtrueRefs'
import { closeThis } from 'assets/resources/comtrue/js/default/common'

import IconNaviClose from 'assets/resources/comtrue/img/icon/icon-navi-close.png'
import bright from 'assets/resources/comtrue/img/icon/bright.png'
import focus from 'assets/resources/comtrue/img/icon/focus.png'
import fakeCard from 'assets/resources/comtrue/img/icon/idcard.png'
import idcard_guide from 'assets/resources/comtrue/img/icon/idcard_guide.png'
import reflection_exhibit from 'assets/resources/comtrue/img/icon/reflection_exhibit.png'
import { comUtil } from 'app/shared/utils'

/*======================================================================
 * @ 업무설명 : 안면인증 컴트루 솔루션 호출 - 신분증
 * @ 파 일 명 : src\app\pages\com\ocr\idcard.jsx
 * @ 화면 ID  :
 * @ASIS :
 *======================================================================
 * 번호  작업자           작업일         변경내용
 *======================================================================
 * 1     최정기(S24068)   2025.06.19    최초작성
 *======================================================================
 */
const debounce = (func, wait) => {
    let timeout
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout)
            func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}

const IdCardOCR = () => {
    const { cardType = 'general' } = useNavigate()

    // const COMTRUEVAR = useComtrue();
    const ComtrueCamera = useComtrueCamera()

    const COMTRUEOcrCommon = useCOMTRUEOcrCommon()

    const videoRef = useRef(null)
    const canvasRef = useRef(null)

    // [2025.06.10] dom 조작 제거 및 ref 추가
    const rectangleEdgeRefs = useRef([])
    const bottomTextRef = useRef(null)
    const boxTopRef = useRef(null)
    const boxContainerRef = useRef(null)
    const rectangleCursorRef = useRef(null)
    const resultFormGroupRef = useRef(null)
    const modalContentRef = useRef(null)
    const comment2Refs = useRef([])
    const guideMessageTextRef = useRef(null)

    // refs 객체 생성 추가
    const refs = {
        videoComtrue: videoRef, // COMTRUEVAR.videoComtrue
        canvasComtrue: canvasRef, // COMTRUEVAR.canvasComtrue
        rectangleEdgeRefsComtrue: rectangleEdgeRefs,
        bottomTextRefComtrue: bottomTextRef,
        boxTopRefComtrue: boxTopRef,
        boxContainerRefComtrue: boxContainerRef,
        rectangleCursorRefComtrue: rectangleCursorRef,
        resultFormGroupRefComtrue: resultFormGroupRef,
        modalContentRefComtrue: modalContentRef,
        comment2RefsComtrue: comment2Refs,
        guideMessageTextRefComtrue: guideMessageTextRef,
    }
    useComtrueRefs(refs)

    const getTitleText = (type) => {
        switch (type) {
            case 'jumin':
                return '주민등록증 촬영'
            case 'driver':
                return '운전면허증 촬영'
            case 'passport':
            case 'kpassport':
                return '여권 촬영'
            case 'foreignPassport':
                return '해외 여권 촬영'
            case 'foreigner':
                return '외국인등록증 촬영'
            case 'creditcard':
                return '신용카드 촬영'
            case 'usim':
            case 'usim_small':
                return '유심 촬영'
            case 'imei':
                return 'IMEI 촬영'
            case 'general':
                return '신분증 촬영'
            default:
                return '신분증 촬영'
        }
    }

    const getGuideText = (type) => {
        switch (type) {
            case 'jumin':
                return '주민등록증을 놓아주세요'
            case 'driver':
                return '운전면허증을 놓아주세요'
            case 'passport':
            case 'kpassport':
            case 'foreignPassport':
                return '여권을 놓아주세요'
            case 'foreigner':
                return '외국인등록증을 놓아주세요'
            case 'creditcard':
                return '신용카드를 놓아주세요'
            case 'usim':
            case 'usim_small':
                return '유심카드를 놓아주세요'
            case 'imei':
                return 'IMEI 화면을 촬영해주세요'
            default:
                return '자동으로 촬영됩니다'
        }
    }

    useEffect(() => {
        comUtil.setUserData('globalLoading', false)

        document.title = getTitleText(cardType)

        COMTRUEVAR.cardTypeVarComtrue = 'general'

        // 초기 크기 조정
        const initialResize = () => {
            COMTRUEOcrCommon.resizeCardFrame(cardType)
            COMTRUEOcrCommon.videoResize()
        }

        const handleResize = debounce(() => {
            COMTRUEOcrCommon.resizeCardFrame(cardType)
            COMTRUEOcrCommon.videoResize()
        }, 250)

        const handleScroll = debounce(() => {
            COMTRUEOcrCommon.resizeCardFrame(cardType)
            COMTRUEOcrCommon.videoResize()
        }, 250)

        const handleTouchEnd = debounce(() => {
            COMTRUEOcrCommon.resizeCardFrame(cardType)
            COMTRUEOcrCommon.videoResize()
        }, 250)

        window.addEventListener('resize', handleResize)
        window.addEventListener('scroll', handleScroll)
        window.addEventListener('touchend', handleTouchEnd)

        // DOM 로드 완료 체크 및 이벤트 리스너 등록
        if (document.readyState === 'complete') {
            const ibkScreen = document.getElementById('ibkScreen')
            const comtrueScreen = document.getElementById('comtrueScreen')

            initialResize()
        } else {
            window.addEventListener('load', initialResize)
        }

        // 비디오 로드 완료 이벤트 리스너
        const videoElement = videoRef.current
        if (videoElement) {
            videoElement.addEventListener('loadeddata', initialResize)
        }

        ComtrueCamera.cameraSettings(
            COMTRUEVAR.videoComtrue,
            COMTRUEVAR.canvasComtrue,
            false,
            COMTRUEVAR.useWebSocket,
            'ocr',
            () => {
                COMTRUEOcrCommon.cameraStart(cardType)
                initialResize()
            },
            COMTRUEVAR.cardTypeVarComtrue
        )

        return () => {
            window.removeEventListener('load', initialResize)
            if (videoRef.current) {
                videoRef.current.removeEventListener(
                    'loadeddata',
                    initialResize
                )
            }
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('touchend', handleTouchEnd)
        }
    }, [cardType])

    return (
        <>
            <div className="theme-box" id="ibkScreen">
                <div className="ibk-loading">
                    <div className="ibk-loading-box">
                        <img
                            src="/assets/images/common/icon-loading-ani.svg"
                            alt="로딩중"
                            width={92}
                            height={90}
                        />
                        <h2 className="h4 mt-32">
                            카메라를 불러오는 중입니다.
                        </h2>
                        <p className="mt-04">잠시만 기다려주세요.</p>
                    </div>
                </div>
            </div>
            <div
                className="theme-ekyc"
                style={{ display: 'none' }}
                id="comtrueScreen"
            >
                <div className="nav-top">
                    <div className="nav-left">
                        <div className="Title">
                            {/*<img src="/static/img/icon/icon-navi-dot.png" height="24" alt="nav dot"/>*/}
                            {/*<span className="Title-Text">{getTitleText(cardType)}</span>*/}
                        </div>
                    </div>
                    <div className="nav-right">
                        <img
                            src={IconNaviClose}
                            height="32"
                            alt="X"
                            onClick={() => closeThis()}
                        />
                        {/* <Image
                        src={IconNaviClose}
                        fallback={IconNaviClose}
                        direction="x"
                        width={230}
                        height={146}
                        alt=""
                        onClick={() => closeThis()}
                    /> */}
                    </div>
                </div>

                <div className="camera" style={{ position: 'fixed' }}>
                    <div
                        className="block-video"
                        style={{ display: 'contents', textAlign: 'center' }}
                    >
                        <video
                            ref={videoRef}
                            id="camera-video"
                            style={{
                                zIndex: 0,
                                scale: 1,
                                width: 'auto',
                                height: '125%',
                                verticalAlign: 'middle',
                            }}
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                        <canvas
                            ref={canvasRef}
                            id="camera-canvas"
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div
                        className="blind-layout box-container"
                        id="box-container"
                        ref={boxContainerRef}
                    >
                        {/* 기존소스 <div
                        className="box_ box-top box-blind scan-comment-wrapper scan-comment"
                        ref={boxTopRef}
                    > */}
                        <div
                            className="box_ box-blind scan-comment-wrapper scan-comment"
                            ref={boxTopRef}
                        >
                            <div
                                className="comment_2"
                                ref={(el) => (comment2Refs.current[0] = el)}
                                style={{
                                    fontSize: '18px',
                                    color: '#ffffff',
                                    font: 'Pretendard',
                                }}
                            >
                                신분증을 영역에 맞추면
                            </div>
                            <div
                                className="comment_2"
                                ref={(el) => (comment2Refs.current[1] = el)}
                                style={{
                                    fontSize: '18px',
                                    color: '#ffffff',
                                    font: 'Pretendard',
                                }}
                            >
                                {getGuideText(cardType)}
                            </div>
                            {/* <div id="bottom_text" ref={bottomTextRef}></div> 
                        <div
                            id="guide-message-text"
                            ref={guideMessageTextRef}
                            style={{
                                fontSize: '16pt',
                                color: '#ffffff',
                                font: 'Pretendard',
                            }}
                        />*/}
                        </div>

                        <div className="middle-left_ box-left box-blind"></div>
                        <div className="rectangle box-middle" id="box-middle">
                            <div
                                className="rectangle-cursor"
                                style={{ textAlign: 'center' }}
                                ref={rectangleCursorRef}
                            >
                                <div className="rectangle-cursor-bg"></div>
                                <div className="rectangle-cursor-img">
                                    <div className="rectangle-view">
                                        <div className="rectangle-cursor-list"></div>
                                        <div className="rectangle-cursor-list"></div>
                                        <div className="rectangle-cursor-list"></div>
                                        <div className="rectangle-cursor-list"></div>
                                        <div className="scanning-line" />
                                        <div className="scanning-line-right" />
                                    </div>
                                </div>

                                <div className="rectangle-cursor-bg"></div>
                                {/*<img
                                    src={RectangleCursorImg}
                                    height="194"
                                    alt=""
                            /> */}
                                {/* <div
                                    className="rectangle-edge"
                                    ref={(el) =>
                                        (rectangleEdgeRefs.current[0] = el)
                                    }
                                ></div>
                                <div
                                    className="rectangle-edge"
                                    ref={(el) =>
                                        (rectangleEdgeRefs.current[1] = el)
                                    }
                                ></div>
                                <div
                                    className="rectangle-edge"
                                    ref={(el) =>
                                        (rectangleEdgeRefs.current[2] = el)
                                    }
                                ></div>
                                <div
                                    className="rectangle-edge"
                                    ref={(el) =>
                                        (rectangleEdgeRefs.current[3] = el)
                                    }
                                ></div>
                                <div className="scanning-line" /> */}
                            </div>
                        </div>
                        <div className="middle-right_ box-right box-blind">
                            &nbsp;
                        </div>

                        <div className="box_ wrapper_ box-bottom box-blind">
                            <div className="nav-center">
                                <div
                                    id="bright"
                                    style={{
                                        display: '',
                                        color: '#ffffff',
                                        font: 'Pretendard',
                                        fontWeight: 'medium',
                                        fontSize: '16pt',
                                        lineHeight: '24px',
                                    }}
                                >
                                    <img
                                        src={bright}
                                        height="56"
                                        alt="선명한 촬영을 위해 초점을 맞춰주세요"
                                    />
                                    <p>선명한 촬영을 위해 초점을 맞춰주세요</p>
                                </div>
                                <div
                                    id="focus"
                                    style={{
                                        display: 'none',
                                        color: '#ffffff',
                                        font: 'Pretendard',
                                        fontWeight: 'medium',
                                        fontSize: '16pt',
                                        lineHeight: '24px',
                                    }}
                                >
                                    <img
                                        src={focus}
                                        height="56"
                                        alt="밝은 장소에서 촬영해주세요"
                                    />
                                    <p>밝은 장소에서 촬영해주세요</p>
                                </div>
                                <div
                                    id="fakeCard"
                                    style={{
                                        display: 'none',
                                        color: '#ffffff',
                                        font: 'Pretendard',
                                        fontWeight: 'medium',
                                        fontSize: '16pt',
                                        lineHeight: '24px',
                                    }}
                                >
                                    <img
                                        src={fakeCard}
                                        height="56"
                                        alt="실물 신분증을 촬영해주십시오."
                                    />
                                    <p>실물 신분증을 촬영해주세요</p>
                                </div>
                                <div
                                    id="cutReflect"
                                    style={{
                                        display: 'none',
                                        color: '#ffffff',
                                        font: 'Pretendard',
                                        fontWeight: 'medium',
                                        fontSize: '16pt',
                                        lineHeight: '24px',
                                    }}
                                >
                                    <img
                                        src={reflection_exhibit}
                                        height="56"
                                        alt="잘리거나 빛 반사가 되지 않도록 해주세요."
                                    />
                                    <p>
                                        잘리거나 빛 반사가 되지 않도록 해주세요.
                                    </p>
                                </div>
                                <div
                                    id="lineGiude"
                                    style={{
                                        display: 'none',
                                        color: '#ffffff',
                                        font: 'Pretendard',
                                        fontWeight: 'medium',
                                        fontSize: '16pt',
                                        lineHeight: '24px',
                                    }}
                                >
                                    <img
                                        src={idcard_guide}
                                        height="56"
                                        alt="신분증 앞면을 가이드 영역에 맞춰주세요."
                                    />
                                    <p>
                                        신분증 앞면을 가이드 영역에 맞춰주세요.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        id="output"
                        style={{ position: 'absolute', display: 'none' }}
                    />
                </div>

                {/* Preview Modal */}
                <div
                    className="modal fade"
                    id="preview"
                    tabIndex="-1"
                    role="banner"
                    data-backdrop="false"
                    data-keyboard="false"
                >
                    <div className="modal-dialog" role="document">
                        <div
                            className="modal-content"
                            id="modal-content"
                            style={{ height: '100%' }}
                            ref={modalContentRef}
                        >
                            <div
                                className="modal-body"
                                style={{ flex: 'initial' }}
                            >
                                <div id="preview-result">
                                    <div id="preview-result-title">
                                        정보 확인
                                    </div>
                                    <div
                                        className="resultFormGroup"
                                        id="resultFormGroup"
                                        ref={resultFormGroupRef}
                                    >
                                        <div id="preview-result-image">
                                            <img
                                                src="assets/resources/comtrue/img/logo/card_sample.jpeg"
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                }}
                                                alt="card sample"
                                            />
                                        </div>
                                        <form
                                            action=""
                                            onSubmit={(e) => e.preventDefault()}
                                            id="resultForm"
                                        >
                                            <div
                                                id="preview-result-div"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                            />
                                        </form>
                                    </div>
                                </div>
                                {/* fixme [2025.06.09] 미사용 주석처리 */}
                                {/*<div className="w-100 modal-btn-wrapper">
                                <button
                                    type="button"
                                    className="button-gray button-modal"
                                    onClick={() => COMTRUECommon.retry()}
                                >
                                    재촬영하기
                                </button>
                                <div/>
                                <button
                                    type="button"
                                    className="button-black button-modal"
                                    onClick={() => COMTRUEOcrCommon.submitEdit()}
                                >
                                    확인
                                </button>
                                <div className="modal-gradation">&nbsp;</div>
                            </div>*/}
                            </div>
                        </div>
                    </div>
                </div>

                {/* <div id="bottom-bar">aiSee OCR</div> */}
                <div id="bottom-bar"></div>
            </div>
        </>
    )
}

export default IdCardOCR
